/**
 * FILE MENU & CATEGORIES MANAGER — UI dropdown «Файл», модалка «Управление категориями»
 * с drag-and-drop сортировкой, CRUD категорий, выбор папки сохранения.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: FILE MENU & MY ESTIMATES).
 *
 * Группы методов:
 *
 * 1. File menu (dropdown):
 *    - toggleFileMenu()
 *
 * 2. Categories manager modal:
 *    - showCategoriesManager / hideCategoriesManager
 *    - switchSettingsTab / renderCategoriesManager / updateReorganizationStatus
 *    - manualReorganizeCategories / manualReorganizeTemplates
 *    - resetCategoryStats / resetTemplateStats
 *
 * 3. Drag & Drop сортировка категорий:
 *    - handleDragStart / handleDragOver / handleDragEnter
 *    - handleDragLeave / handleDrop / handleDragEnd
 *
 * 4. Categories CRUD:
 *    - addCategoryFromManager / deleteCategory
 *    - showCategoryModal / hideCategoryModal / addCategory
 *    - renderCategoryOptions
 *
 * 5. Catalog folder picker (File System Access API):
 *    - selectCatalogFolder / updateCatalogFolderDisplay
 *
 * @module app/file-menu-categories
 */

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет file-menu / categories-manager методы к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachFileMenuCategories(ClassRef) {
    Object.assign(ClassRef.prototype, {
        toggleFileMenu() {
            const menu = document.getElementById('file-dropdown-menu');
            if (menu) {
                const isVisible = menu.style.display !== 'none';
                menu.style.display = isVisible ? 'none' : 'block';

                // Закрыть меню при клике вне его
                if (!isVisible) {
                    setTimeout(() => {
                        const closeMenu = (e) => {
                            if (!e.target.closest('#file-dropdown-menu') && !e.target.closest('#file-menu-btn')) {
                                menu.style.display = 'none';
                                document.removeEventListener('click', closeMenu);
                            }
                        };
                        document.addEventListener('click', closeMenu);
                    }, 0);
                }
            }
        },

        showCategoriesManager() {
            // Открываем модальное окно для управления категориями
            const modal = document.getElementById('categories-manager-modal');
            if (modal) {
                this.renderCategoriesManager();
                this.updateReorganizationStatus();
                this.updateCatalogFolderDisplay();

                // Привязываем обработчик кнопки выбора папки (один раз)
                const folderBtn = document.getElementById('select-folder-btn');
                if (folderBtn) {
                    // Удаляем старые обработчики если были
                    const oldHandler = folderBtn._folderHandler;
                    if (oldHandler) {
                        folderBtn.removeEventListener('click', oldHandler);
                        console.log('Removed old folder button listener');
                    }

                    // Создаем новый обработчик
                    const newHandler = async (e) => {
                        console.log('Folder button clicked');

                        // Останавливаем всплытие события
                        e.preventDefault();
                        e.stopPropagation();

                        // Защита от двойного клика - отключаем кнопку
                        if (folderBtn.disabled) {
                            console.log('Button already disabled, ignoring click');
                            return;
                        }
                        folderBtn.disabled = true;
                        folderBtn.style.opacity = '0.5';

                        const modal = document.getElementById('categories-manager-modal');

                        try {
                            // ВАЖНО: вызываем picker СРАЗУ, без setTimeout!
                            // Это сохраняет контекст user activation
                            await this.selectCatalogFolder();

                            // После успешного выбора обновляем UI
                            this.updateCatalogFolderDisplay();

                        } catch (err) {
                            console.error('Folder selection error:', err);
                        } finally {
                            // Включаем кнопку обратно
                            folderBtn.disabled = false;
                            folderBtn.style.opacity = '1';
                        }
                    };

                    folderBtn.addEventListener('click', newHandler);
                    folderBtn._folderHandler = newHandler;
                    console.log('Folder button listener attached');
                }

                // Рендерим список регионов
                this.renderRegionsManager();

                modal.classList.add('show');
            }
        },

        updateReorganizationStatus() {
            const statusElement = document.getElementById('reorganization-status');
            if (!statusElement) return;

            if (this.categoryStats.lastReorganization) {
                const lastReorg = new Date(this.categoryStats.lastReorganization);
                const now = new Date();
                const diffMs = now - lastReorg;
                const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                let timeStr = '';
                if (diffDays > 0) {
                    timeStr = _t('app.file_menu.time_days_ago', '{days} d {hours} h ago')
                        .replace('{days}', diffDays).replace('{hours}', diffHours);
                } else if (diffHours > 0) {
                    timeStr = _t('app.file_menu.time_hours_ago', '{hours} h ago').replace('{hours}', diffHours);
                } else {
                    timeStr = _t('app.file_menu.time_just_now', 'just now');
                }

                statusElement.textContent = _t('app.file_menu.reorg_last', 'Last run: {time}').replace('{time}', timeStr);
            } else {
                statusElement.textContent = _t('app.file_menu.reorg_last_never', 'Last run: never');
            }

            // Update template reorganization status
            const templateStatusElement = document.getElementById('template-reorganization-status');
            if (templateStatusElement) {
                if (this.templateStats.lastReorganization) {
                    const lastReorg = new Date(this.templateStats.lastReorganization);
                    const now = new Date();
                    const diffMs = now - lastReorg;
                    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                    let timeStr = '';
                    if (diffDays > 0) {
                        timeStr = _t('app.file_menu.time_days_ago', '{days} d {hours} h ago')
                            .replace('{days}', diffDays).replace('{hours}', diffHours);
                    } else if (diffHours > 0) {
                        timeStr = _t('app.file_menu.time_hours_ago', '{hours} h ago').replace('{hours}', diffHours);
                    } else {
                        timeStr = _t('app.file_menu.time_just_now', 'just now');
                    }

                    templateStatusElement.textContent = _t('app.file_menu.reorg_last', 'Last run: {time}').replace('{time}', timeStr);
                } else {
                    templateStatusElement.textContent = _t('app.file_menu.reorg_last_never', 'Last run: never');
                }
            }
        },

        async manualReorganizeCategories() {
            const totalUsage = Object.values(this.categoryStats.usage).reduce((sum, count) => sum + count, 0);

            if (totalUsage === 0) {
                this.showNotification(_t('app.file_menu.no_data_reorg', 'Not enough data to reorganize. Add services from the catalog first.'), true);
                return;
            }

            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.file_menu.reorg_confirm_categories', 'Reorganize categories by usage popularity?')
            });
            if (ok) {
                this.reorganizeCategoriesByPopularity();
                this.renderCategoriesManager();
                this.updateReorganizationStatus();
                this.showNotification(_t('app.file_menu.reorg_done_categories', 'Categories reorganized by popularity'));
            }
        },

        async manualReorganizeTemplates() {
            const totalUsage = Object.values(this.templateStats.usage).reduce((sum, count) => sum + count, 0);

            if (totalUsage === 0) {
                this.showNotification(_t('app.file_menu.no_data_reorg_templates', 'Not enough data to reorganize services. Add catalog services to a quote first.'), true);
                return;
            }

            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.file_menu.reorg_confirm_templates', 'Reorganize services within categories by usage popularity?')
            });
            if (ok) {
                this.reorganizeTemplatesByPopularity();
                this.updateReorganizationStatus();
                this.showNotification(_t('app.file_menu.reorg_done_templates', 'Services reorganized by popularity'));
            }
        },

        async resetCategoryStats() {
            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.file_menu.reset_stats_confirm_categories', 'Reset local category usage statistics?\n\nThis resets sort counters for categories.\nGlobal analytics statistics will be preserved.\n\nContinue?'),
                danger: true
            });
            if (!ok) return;

            // Reset all usage counters to 0 (только локальная статистика)
            this.categories.forEach(cat => {
                this.categoryStats.usage[cat.id] = 0;
            });

            // Reset reorganization timestamp
            this.categoryStats.lastReorganization = null;

            // Save to localStorage
            this.saveCategoryStats();

            // ВАЖНО: Глобальная статистика (this.globalStats) НЕ сбрасывается!
            // Она используется для долгосрочной аналитики и должна накапливаться всегда.

            // Update UI
            this.renderCategoriesManager();
            this.updateReorganizationStatus();

            console.log('Local category stats reset:', this.categoryStats);
            console.log('Global stats preserved:', this.globalStats);
            this.showNotification(_t('app.file_menu.reset_stats_done_categories', 'Local category statistics reset. Global statistics preserved.'));
        },

        async resetTemplateStats() {
            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.file_menu.reset_stats_confirm_templates', 'Reset local service usage statistics?\n\nThis resets sort counters within categories.\nGlobal analytics statistics will be preserved.\n\nContinue?'),
                danger: true
            });
            if (!ok) return;

            // Reset all template usage counters to 0 (только локальная статистика)
            this.templates.forEach(tpl => {
                this.templateStats.usage[tpl.id] = 0;
            });

            // Reset reorganization timestamp
            this.templateStats.lastReorganization = null;

            // Save to localStorage
            this.saveTemplateStats();

            // ВАЖНО: Глобальная статистика (this.globalStats) НЕ сбрасывается!
            // Она используется для долгосрочной аналитики и должна накапливаться всегда.

            // Update UI
            this.updateReorganizationStatus();

            console.log('Local template stats reset:', this.templateStats);
            console.log('Global stats preserved:', this.globalStats);
            this.showNotification(_t('app.file_menu.reset_stats_done_templates', 'Local service statistics reset. Global statistics preserved.'));
        },

        hideCategoriesManager() {
            const modal = document.getElementById('categories-manager-modal');
            if (modal) {
                modal.classList.remove('show');
            }
        },

        switchSettingsTab(tabName) {
            // Скрыть все вкладки
            const allTabs = document.querySelectorAll('.settings-tab');
            const allContents = document.querySelectorAll('.settings-tab-content');

            allTabs.forEach(tab => {
                const isActive = tab.dataset.tab === tabName;
                if (isActive) {
                    tab.style.borderBottomColor = 'var(--primary)';
                    tab.style.color = 'var(--primary)';
                    tab.style.fontWeight = '600';
                    tab.classList.add('active');
                } else {
                    tab.style.borderBottomColor = 'transparent';
                    tab.style.color = 'var(--gray-500)';
                    tab.style.fontWeight = '500';
                    tab.classList.remove('active');
                }
            });

            allContents.forEach(content => {
                content.style.display = content.dataset.tab === tabName ? 'block' : 'none';
            });
        },

        renderCategoriesManager() {
            const container = document.getElementById('categories-manager-list');
            if (!container) return;

            // В менеджере показываем ВСЕ категории, включая "Не определено"
            // Это настройки - пользователь должен видеть все доступные категории
            const visibleCategories = this.categories;

            container.innerHTML = visibleCategories.map((cat, index) => {
                const popularity = this.getCategoryPopularity(cat.id);
                const usageLevel = this.getCategoryUsageLevel(cat.id);
                const usage = this.categoryStats.usage[cat.id] || 0;

                // Color based on usage level
                let popularityColor = '#9ca3af'; // gray - none
                if (usageLevel === 'high') popularityColor = '#16a34a'; // green
                else if (usageLevel === 'medium') popularityColor = '#eab308'; // yellow
                else if (usageLevel === 'low') popularityColor = '#3b82f6'; // blue

                // Системные категории нельзя перетаскивать и удалять
                const isDraggable = !cat.system;
                const dragCursor = isDraggable ? 'move' : 'default';
                const dragIcon = isDraggable ? '☰' : '🔒';
                const systemBadge = cat.system ? `<span style="font-size: 10px; color: #6b7280; background: #f3f4f6; padding: 2px 6px; border-radius: 3px; margin-left: 6px;">${_t('app.file_menu.system_badge', 'system')}</span>` : '';
                const systemTooltip = cat.system ? _t('app.file_menu.system_tooltip', 'System category. Shown in catalog only when it contains services.') : '';

                return `
                    <div class="category-manager-item"
                         data-category-id="${cat.id}"
                         data-index="${index}"
                         draggable="${isDraggable}"
                         title="${systemTooltip}"
                         style="display: flex; justify-content: space-between; align-items: center; padding: 4px 8px; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 4px; cursor: ${dragCursor}; background: white; transition: all 0.2s;">
                        <span style="font-size: 12px; display: flex; align-items: center; gap: 6px;">
                            <span style="color: #9ca3af; font-size: 14px;">${dragIcon}</span>
                            <span style="font-size: 14px;">${this.renderIcon(cat.icon, '14px')}</span>
                            <span>${cat.name}</span>
                            ${systemBadge}
                        </span>
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <span style="font-size: 10px; color: ${popularityColor}; font-weight: 600; display: flex; align-items: center; gap: 3px;">
                                ${popularity}% (${usage})
                            </span>
                            ${isDraggable ? `
                                <button onclick="window.QuoteCalc.deleteCategory('${cat.id}')"
                                        style="width: 20px; height: 20px; border: none; background: transparent; color: #9ca3af; cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center; border-radius: 3px; transition: all 0.15s;"
                                        onmouseover="this.style.background='#fee2e2'; this.style.color='#dc2626';"
                                        onmouseout="this.style.background='transparent'; this.style.color='#9ca3af';"
                                        title="${_t('app.file_menu.delete_category_title', 'Delete category')}">×</button>
                            ` : '<div style="width: 20px;"></div>'}
                        </div>
                    </div>
                `;
            }).join('');

            // Add drag and drop event listeners
            const items = container.querySelectorAll('.category-manager-item');
            items.forEach(item => {
                item.addEventListener('dragstart', (e) => this.handleDragStart(e));
                item.addEventListener('dragover', (e) => this.handleDragOver(e));
                item.addEventListener('drop', (e) => this.handleDrop(e));
                item.addEventListener('dragenter', (e) => this.handleDragEnter(e));
                item.addEventListener('dragleave', (e) => this.handleDragLeave(e));
                item.addEventListener('dragend', (e) => this.handleDragEnd(e));
            });

            // Инициализация Lucide иконок
            lucide.createIcons();
        },

        handleDragStart(e) {
            this.draggedElement = e.target.closest('.category-manager-item');
            this.draggedElement.style.opacity = '0.4';
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/html', this.draggedElement.innerHTML);
        },

        handleDragOver(e) {
            if (e.preventDefault) {
                e.preventDefault();
            }
            e.dataTransfer.dropEffect = 'move';
            return false;
        },

        handleDragEnter(e) {
            const target = e.target.closest('.category-manager-item');
            if (target && target !== this.draggedElement) {
                target.style.borderColor = '#2563eb';
                target.style.background = '#eff6ff';
            }
        },

        handleDragLeave(e) {
            const target = e.target.closest('.category-manager-item');
            if (target && target !== this.draggedElement) {
                target.style.borderColor = '#e5e7eb';
                target.style.background = 'white';
            }
        },

        handleDrop(e) {
            if (e.stopPropagation) {
                e.stopPropagation();
            }

            const target = e.target.closest('.category-manager-item');
            if (this.draggedElement !== target && target) {
                const draggedIndex = parseInt(this.draggedElement.dataset.index);
                const targetIndex = parseInt(target.dataset.index);

                // Reorder categories array
                const movedItem = this.categories.splice(draggedIndex, 1)[0];
                this.categories.splice(targetIndex, 0, movedItem);

                // Save and re-render
                this.saveCategories();
                this.renderCategories();
                this.renderCategoryOptions();
                this.renderCategoriesManager();
                this.showNotification(_t('app.file_menu.order_changed', 'Category order updated'));
            }

            return false;
        },

        handleDragEnd(e) {
            const items = document.querySelectorAll('.category-manager-item');
            items.forEach(item => {
                item.style.opacity = '1';
                item.style.borderColor = '#e5e7eb';
                item.style.background = 'white';
            });
        },

        async deleteCategory(categoryId) {
            // Используем выбранный регион для просмотра
            const targetRegion = this.selectedRegionForView || this.currentRegion;

            // Загружаем категории целевого региона
            const categoriesKey = `quoteCalc_categories_${targetRegion}`;
            let categories = JSON.parse(localStorage.getItem(categoriesKey) || '[]');

            // Проверка: нельзя удалить системную категорию
            const category = categories.find(c => c.id === categoryId);
            if (category && category.system) {
                this.showNotification(_t('app.file_menu.system_cannot_delete', 'System category cannot be deleted'), true);
                return;
            }

            const { confirmModal } = await import('/public/js/components/text-prompt-modal.js');
            const ok = await confirmModal({
                title: _t('app.file_menu.delete_category_confirm_title', 'Delete category?'),
                message: _t('app.file_menu.delete_category_confirm_msg', 'All services in this category will be moved to "Undefined".'),
                danger: true
            });
            if (!ok) return;

            // Загружаем услуги целевого региона
            const templatesKey = `quoteCalc_templates_${targetRegion}`;
            let templates = JSON.parse(localStorage.getItem(templatesKey) || '[]');

            // Переместить все услуги из удаляемой категории в "Не определено"
            const affectedTemplates = templates.filter(t => t.category === categoryId);
            console.log(`Found ${affectedTemplates.length} templates in category "${categoryId}" in region "${targetRegion}"`);

            if (affectedTemplates.length > 0) {
                affectedTemplates.forEach(template => {
                    console.log(`Moving template "${template.name}" from "${template.category}" to "undefined"`);
                    template.category = 'undefined';
                });
                localStorage.setItem(templatesKey, JSON.stringify(templates));

                // Убеждаемся что категория "Не определено" существует
                const hasUndefinedCategory = categories.some(cat => cat.id === 'undefined');
                if (!hasUndefinedCategory) {
                    console.log('Добавляем категорию "Не определено" для перемещенных услуг');
                    categories.unshift({
                        id: 'undefined',
                        name: _t('app.file_menu.category_undefined', 'Undefined'),
                        icon: '❓',
                        system: true
                    });
                    localStorage.setItem(categoriesKey, JSON.stringify(categories));
                }

                // Если это текущий регион - обновляем локальные данные
                if (targetRegion === this.currentRegion) {
                    this.templates = templates;
                    this.categories = categories; // Обновляем категории тоже!
                    this.renderTemplates();
                }

                console.log(`Перемещено ${affectedTemplates.length} услуг в категорию "Не определено"`);
            }

            // Удалить категорию
            console.log(`Deleting category: ${categoryId} from region "${targetRegion}"`);
            categories = categories.filter(c => c.id !== categoryId);
            localStorage.setItem(categoriesKey, JSON.stringify(categories));

            // Если это текущий регион - обновляем локальные категории
            if (targetRegion === this.currentRegion) {
                this.categories = categories;
                this.renderCategories();
                this.renderCategoryOptions();
            }

            // Обновляем отображение категорий
            this.loadCategoriesForRegionView(targetRegion);

            this.showNotification(
                _t('app.file_menu.category_deleted_status', 'Category removed from region "{region}". Services moved: {count}')
                    .replace('{region}', targetRegion)
                    .replace('{count}', affectedTemplates.length)
            );
        },

        addCategoryFromManager() {
            const nameElement = document.getElementById('category-manager-name');
            const iconElement = document.getElementById('category-manager-icon');

            if (!nameElement || !iconElement) return;

            const name = nameElement.value.trim();
            const icon = iconElement.value;

            if (!name) {
                this.showNotification(_t('app.file_menu.enter_category_name', 'Enter category name'), true);
                return;
            }

            // Используем выбранный регион для просмотра (или текущий)
            const targetRegion = this.selectedRegionForView || this.currentRegion;

            // Загружаем категории целевого региона
            const categoriesKey = `quoteCalc_categories_${targetRegion}`;
            let categories = JSON.parse(localStorage.getItem(categoriesKey) || '[]');

            // Генерируем ID из названия, для кириллицы используем timestamp
            let categoryId = name.toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '');

            // Если после очистки ID пустой (например, кириллическое название),
            // используем timestamp для уникальности
            if (!categoryId || categoryId === '') {
                categoryId = 'cat_' + Date.now();
            }

            // Check if category already exists
            const exists = categories.find(c =>
                c.name.toLowerCase() === name.toLowerCase() ||
                c.id === categoryId
            );

            if (exists) {
                this.showNotification(_t('app.file_menu.category_exists', 'This category already exists'), true);
                return;
            }

            const category = {
                id: categoryId,
                name: name,
                icon: icon
            };

            console.log('Creating new category in region:', targetRegion, category);

            // Добавляем категорию в целевой регион
            categories.push(category);
            localStorage.setItem(categoriesKey, JSON.stringify(categories));

            // Если это текущий регион - обновляем локальные категории
            if (targetRegion === this.currentRegion) {
                this.categories = categories;
                this.renderCategories();
                this.renderCategoryOptions();
            }

            // Обновляем отображение категорий
            this.loadCategoriesForRegionView(targetRegion);

            // Clear inputs
            nameElement.value = '';
            iconElement.selectedIndex = 0;

            this.showNotification(_t('app.file_menu.category_added', 'Category added'));
        },

        renderCategoryOptions() {
            const select = document.getElementById('catalog-category');
            if (!select) return;

            // Фильтруем системные категории - их нельзя выбирать вручную при создании услуг
            const selectableCategories = this.categories.filter(cat => !cat.system);

            select.innerHTML = selectableCategories.map(cat => `
                <option value="${cat.id}">${cat.icon} ${cat.name}</option>
            `).join('');
        },

        showCategoryModal() {
            const modal = document.getElementById('category-modal');
            if (modal) {
                modal.classList.add('show');
                setTimeout(() => {
                    const nameInput = document.getElementById('category-name');
                    if (nameInput) nameInput.focus();
                }, 300);
            }
        },

        hideCategoryModal() {
            const modal = document.getElementById('category-modal');
            if (modal) {
                modal.classList.remove('show');
                const nameInput = document.getElementById('category-name');
                if (nameInput) nameInput.value = '';
            }
        },

        addCategory() {
            const nameElement = document.getElementById('category-name');
            const iconElement = document.getElementById('category-icon');

            if (!nameElement || !iconElement) return;

            const name = nameElement.value.trim();
            const icon = iconElement.value;

            if (!name) {
                this.showNotification(_t('app.file_menu.enter_category_name', 'Enter category name'), true);
                return;
            }

            // Генерируем ID из названия, для кириллицы используем timestamp
            let categoryId = name.toLowerCase()
                .replace(/\s+/g, '_')
                .replace(/[^a-z0-9_]/g, '');

            // Если после очистки ID пустой (например, кириллическое название),
            // используем timestamp для уникальности
            if (!categoryId || categoryId === '') {
                categoryId = 'cat_' + Date.now();
            }

            // Check if category already exists
            const exists = this.categories.find(c =>
                c.name.toLowerCase() === name.toLowerCase() ||
                c.id === categoryId
            );

            if (exists) {
                this.showNotification(_t('app.file_menu.category_exists', 'This category already exists'), true);
                return;
            }

            const category = {
                id: categoryId,
                name: name,
                icon: icon
            };

            console.log('Creating new category:', category);
            this.categories.push(category);
            this.saveCategories();
            this.renderCategories();
            this.renderCategoryOptions();
            this.hideCategoryModal();
            this.showNotification(_t('app.file_menu.category_added', 'Category added'));
        },

        // External catalog loading
        async loadExternalCatalog() {
            // Этот метод больше не используется - каталоги загружаются через loadCatalogForRegion()
            // Оставлен для совместимости со старым кодом
            return;
        },

        // ===== NEW JSON FUNCTIONS =====

        // JSON Catalog functions
        // ===== FILE SYSTEM ACCESS API METHODS =====

        // IndexedDB для сохранения handle папки
        async saveFolderHandleToIndexedDB(handle) {
            return new Promise((resolve, reject) => {
                const request = indexedDB.open('QuoteCalcDB', 1);

                request.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains('folderHandles')) {
                        db.createObjectStore('folderHandles');
                    }
                };

                request.onsuccess = (event) => {
                    const db = event.target.result;
                    const transaction = db.transaction(['folderHandles'], 'readwrite');
                    const store = transaction.objectStore('folderHandles');
                    store.put(handle, 'catalogFolder');

                    transaction.oncomplete = () => {
                        db.close();
                        resolve();
                    };

                    transaction.onerror = () => {
                        db.close();
                        reject(transaction.error);
                    };
                };

                request.onerror = () => reject(request.error);
            });
        },

        async loadFolderHandleFromIndexedDB() {
            return new Promise((resolve, reject) => {
                const request = indexedDB.open('QuoteCalcDB', 1);

                request.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains('folderHandles')) {
                        db.createObjectStore('folderHandles');
                    }
                };

                request.onsuccess = (event) => {
                    const db = event.target.result;
                    const transaction = db.transaction(['folderHandles'], 'readonly');
                    const store = transaction.objectStore('folderHandles');
                    const getRequest = store.get('catalogFolder');

                    getRequest.onsuccess = () => {
                        db.close();
                        resolve(getRequest.result);
                    };

                    getRequest.onerror = () => {
                        db.close();
                        reject(getRequest.error);
                    };
                };

                request.onerror = () => reject(request.error);
            });
        },

        selectCatalogFolder() {
            console.log('selectCatalogFolder called', new Error().stack);

            // Защита от двойного вызова
            if (this._isSelectingFolder) {
                console.log('Folder selection already in progress, ignoring...');
                return Promise.resolve();
            }
            this._isSelectingFolder = true;
            console.log('Starting folder selection...');

            // Используем showDirectoryPicker с дополнительной защитой
            if (!('showDirectoryPicker' in window)) {
                this.showNotification(_t('app.file_menu.folder_browser_unsupported', 'Your browser does not support folder selection. Please use Chrome, Edge or another modern browser.'), true);
                this._isSelectingFolder = false;
                return Promise.resolve();
            }

            // КРИТИЧНО: вызываем showDirectoryPicker СИНХРОННО!
            console.log('Calling showDirectoryPicker synchronously...');
            const pickerPromise = window.showDirectoryPicker({
                mode: 'readwrite',
                startIn: 'downloads'
            });

            // Теперь обрабатываем промис
            return pickerPromise.then(selectedHandle => {
                console.log('Directory selected:', selectedHandle.name);

                // Сохраняем сразу без проверки разрешений (разрешение дается автоматически при выборе)
                this.catalogDirectoryHandle = selectedHandle;
                this.state.saveFolder = selectedHandle.name;
                localStorage.setItem('quoteCalc_catalogFolder', this.state.saveFolder);

                console.log('Folder configured:', this.catalogDirectoryHandle);

                // Сохраняем handle в IndexedDB
                return this.saveFolderHandleToIndexedDB(this.catalogDirectoryHandle);
            }).then(() => {
                this.showNotification(
                    _t('app.file_menu.folder_selected', 'Folder selected: {name}').replace('{name}', this.state.saveFolder)
                );
                this.updateCatalogFolderDisplay();
                this._isSelectingFolder = false;
            }).catch(error => {
                console.error('selectCatalogFolder error:', error);
                this._isSelectingFolder = false;

                if (error.name !== 'AbortError') {
                    this.showNotification(
                        _t('app.file_menu.folder_select_error', 'Folder selection error: {error}').replace('{error}', error.message),
                        true
                    );
                }
            });
        },

        async saveFileToSelectedFolder(filename, content) {
            try {
                console.log('saveFileToSelectedFolder called, handle exists:', !!this.catalogDirectoryHandle);

                if (!this.catalogDirectoryHandle) {
                    throw new Error(_t('app.file_menu.folder_not_set_error', 'No folder selected'));
                }

                // Проверяем разрешение перед сохранением
                const permission = await this.catalogDirectoryHandle.queryPermission({ mode: 'readwrite' });
                console.log('Permission before save:', permission);

                if (permission !== 'granted') {
                    throw new Error(_t('app.file_menu.folder_no_permission', 'No folder access (please reselect)'));
                }

                // Создаём или перезаписываем файл
                const fileHandle = await this.catalogDirectoryHandle.getFileHandle(filename, { create: true });
                const writable = await fileHandle.createWritable();
                await writable.write(content);
                await writable.close();

                console.log('File saved successfully:', filename);
                return true;
            } catch (error) {
                console.error('saveFileToSelectedFolder error:', error);
                throw new Error(
                    _t('app.file_menu.folder_save_error', 'Failed to save file: {error}').replace('{error}', error.message)
                );
            }
        },

        updateCatalogFolderDisplay() {
            const displayElement = document.getElementById('catalog-folder-display');
            const buttonElement = document.getElementById('select-folder-btn');

            if (displayElement) {
                if (this.catalogDirectoryHandle && this.state.saveFolder) {
                    // Папка выбрана и доступ есть
                    displayElement.innerHTML = `
                        <div style="padding: calc(var(--spacing) * 1); background: var(--success-light); border: 1px solid var(--success); border-radius: var(--radius);">
                            <div class="text-xs" style="color: var(--success-dark); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"><i data-lucide="folder" style="width: 12px; height: 12px;"></i> .../${this.state.saveFolder}</div>
                        </div>
                    `;
                    if (buttonElement) {
                        buttonElement.textContent = _t('app.file_menu.folder_change', 'Change folder');
                    }
                } else if (this.state.saveFolder) {
                    // Папка была выбрана, но доступа нет (требуется повторный выбор)
                    const warningText = _t('app.file_menu.folder_warning_reselect', '⚠️ .../{name} (reselect required)').replace('{name}', this.state.saveFolder);
                    displayElement.innerHTML = `
                        <div style="padding: calc(var(--spacing) * 1); background: #fef3c7; border: 1px solid #f59e0b; border-radius: var(--radius);">
                            <div class="text-xs" style="color: #92400e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${warningText}</div>
                        </div>
                    `;
                    if (buttonElement) {
                        buttonElement.textContent = _t('app.file_menu.folder_reselect', 'Reselect folder');
                    }
                } else {
                    // Папка не выбрана
                    displayElement.innerHTML = `
                        <div style="padding: calc(var(--spacing) * 1); background: var(--gray-100); border: 1px solid var(--gray-200); border-radius: var(--radius);">
                            <span class="text-xs text-muted">${_t('app.file_menu.folder_not_selected', 'Not selected')}</span>
                        </div>
                    `;
                    if (buttonElement) {
                        buttonElement.textContent = _t('app.file_menu.folder_select', 'Select folder');
                    }
                }
                // Инициализация Lucide иконок
                lucide.createIcons();
            }
        },
    });
}
